import React, { FC, useRef } from "react";
import { Map } from "immutable";
import { connect } from "react-redux";
import { Waypoint } from "react-waypoint";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import useContainerQuery from "../../../../hooks/useContainerQuery";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  getShipment,
  setSelectedStorageItemId,
  updateComment,
} from "../../../../reduxFolder/reducers/storage";
import { selectedStorageItemId } from "../../../../reduxFolder/selectors/storageSelectors";
import StorageItemRow, { getLayoutTier, RowLayoutTier } from "./StorageItemRow";
import { Spinner, Wrapper } from "./StorageList.styles";

interface StorageListProps {
  disabled: boolean;
  units: string;
  storageItems: any;
  storageTransferringItems: any;
  hasAnyFiltersApplied?: boolean;
  addons: any;
  totalAmount: number;
  selectedParcels: number[];
  selectedStorageItemId: number | null;
  onPagination: () => void;
  handleSelect: (selectParcel: any) => void;
  selectedRangeParcels: (endParcelId: any) => void;
  openRightPanel: OpenRightPanel;
  getShipment: (id: string) => Promise<any>;
  updateComment: (id: string, data: { customer_comment: any }) => Promise<any>;
  setSelectedStorageItemId: (id: number | null) => void;
}

const StorageList: FC<StorageListProps> = React.memo(
  ({
    addons,
    units,
    storageItems,
    storageTransferringItems,
    hasAnyFiltersApplied,
    totalAmount,
    selectedParcels,
    handleSelect,
    selectedRangeParcels,
    onPagination,
    disabled,
    openRightPanel,
    getShipment,
    updateComment,
    selectedStorageItemId,
    setSelectedStorageItemId,
  }) => {
    const hasMore = totalAmount > storageItems.size;

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // The width available to the list changes with both the viewport and the
    // right-panel squeeze, so measure the list itself instead of the screen.
    const layoutTier = useContainerQuery<RowLayoutTier>(
      wrapperRef,
      getLayoutTier,
      "full",
    );

    const handleFocusStorageItemInPanel = (parcel: any) => {
      setSelectedStorageItemId(parcel.get("id"));
      openRightPanel(
        "STORAGE_ITEM",
        Map({
          getShipment,
          updateComment,
          hasInventoryItems: parcel.get("items_quantity", 0) > 1,
          originalItem: parcel,
          isCartVariant: true,
        }).merge(parcel),
      );
    };

    return (
      <Wrapper ref={wrapperRef}>
        {!hasAnyFiltersApplied &&
          storageTransferringItems.map((parcel: any) => (
            <StorageItemRow
              parcel={parcel}
              selectedParcel={selectedStorageItemId}
              onFocusStorageItemInPanel={handleFocusStorageItemInPanel}
              key={parcel.get("id")}
              isChecked={selectedParcels.includes(parcel.get("id"))}
              disabled={disabled}
              units={units}
              addons={addons}
              layoutTier={layoutTier}
              handleSelect={handleSelect}
              selectedRangeParcels={selectedRangeParcels}
            />
          ))}
        {storageItems.map((parcel: any) => (
          <StorageItemRow
            parcel={parcel}
            selectedParcel={selectedStorageItemId}
            onFocusStorageItemInPanel={handleFocusStorageItemInPanel}
            key={parcel.get("id")}
            isChecked={selectedParcels.includes(parcel.get("id"))}
            disabled={disabled}
            units={units}
            addons={addons}
            layoutTier={layoutTier}
            handleSelect={handleSelect}
            selectedRangeParcels={selectedRangeParcels}
          />
        ))}
        {hasMore && (
          <>
            <Waypoint onEnter={onPagination} />
            <Spinner key={0} isActive />
          </>
        )}
      </Wrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    selectedStorageItemId: selectedStorageItemId,
  }),
  {
    openRightPanel,
    getShipment,
    updateComment,
    setSelectedStorageItemId,
  },
);

export default withConnect(StorageList);
